/**
 * The project font context: the project theme (`gui/theme/custom`) and the project default font
 * (`gui/theme/custom_font`). A font lookup reaches them after the font of the node itself. Godot
 * loads both once, in `ThemeDB::initialize_theme` (`scene/theme/theme_db.cpp:53-80`): the theme joins the
 * default theme context, and the font becomes the fallback font and the default theme's `default_font`.
 */
import { warnOnce } from '../../../utils/warnOnce';
import { projectFontPath, projectThemePath, } from '../../../parser/projectSettingsParser';
import { perObject } from '../../../utils/perObject';
export const NO_PROJECT_FONTS = { projectTheme: null, projectDefaultFont: null };
/** The project theme's own copy of a loaded theme, which a scene may also hold. */
const outsideScenes = perObject((theme) => ({
    ...theme,
    isOutsideScenes: true,
}));
/** Owns the failed-load warnings: the lookup runs on every solve pass, and the failure belongs to the file. */
const FAILED_FONT_LOADS = {};
/** The cached value of `path`, or null while it loads (`pending` takes the path) and after it failed. */
function cachedOrPending(path, cache, pending) {
    const cached = cache.getCached(path);
    if (cached === undefined)
        pending?.add(path);
    return cached;
}
/**
 * The font `gui/theme/custom_font` names. A failed load warns once and answers null, so the caller
 * draws the bundled font, as `ThemeDB::initialize_theme` keeps it (`theme_db.cpp:78-79`).
 */
function resolveDefaultFont(path, fonts, pending) {
    const font = cachedOrPending(path, fonts, pending);
    if (font === null) {
        warnOnce(FAILED_FONT_LOADS, path, `[ProjectFont] gui/theme/custom_font names "${path}", which failed to load. Drawing the bundled default font.`);
    }
    return font ?? null;
}
/**
 * The project font context from `settings`, as the caches hold it now. A file still loading lands
 * in `pending`, when a caller passes it to request them. A theme that failed to load is null, as the default theme
 * then answers every lookup.
 *
 * @canonical The project theme and project default font, from the project settings and the caches
 */
export function resolveProjectFonts(settings, { themes, fonts }, pending) {
    const themePath = projectThemePath(settings);
    const fontPath = projectFontPath(settings);
    return {
        projectTheme: themePath ? projectThemeOf(cachedOrPending(themePath, themes, pending?.themes)) : null,
        projectDefaultFont: fontPath ? resolveDefaultFont(fontPath, fonts, pending?.fonts) : null,
    };
}
function projectThemeOf(theme) {
    return theme ? outsideScenes(theme) : null;
}
//# sourceMappingURL=projectFonts.js.map